import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Minus, Plus, ShoppingBag, Truck, Shield, Leaf } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button, Badge, Skeleton } from '@kamalkari/ui';
import { formatPrice } from '@kamalkari/utils';
import { useProductDetailStore } from '@/stores/productDetailStore';
import { useCartStore } from '@/stores/cartStore';
import type { Product, Category } from '@kamalkari/types';

const API_URL = import.meta.env.VITE_API_URL || 'https://0vkok222yh.execute-api.us-east-1.amazonaws.com';

export function ProductDetailModal() {
  const { isOpen, selectedProductId, closeProduct } = useProductDetailStore();
  const { addItem } = useCartStore();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  const { data: product, isLoading: productLoading } = useQuery({
    queryKey: ['product', selectedProductId],
    queryFn: async () => {
      if (!selectedProductId) return null;
      const response = await fetch(`${API_URL}/v1/products/${selectedProductId}`);
      if (!response.ok) throw new Error('Failed to fetch product');
      const json = await response.json();
      return (json.data || json) as Product;
    },
    enabled: !!selectedProductId && isOpen,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/v1/categories`);
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json();
      return data.data?.items || data.items || [];
    },
  });

  const category = product
    ? categories.find((c: Category) => c.categoryId === product.categoryId)
    : null;

  useEffect(() => {
    if (isOpen) {
      setQuantity(1);
      setSelectedImage(0);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleAddToCart = () => {
    if (!product) return;

    addItem({
      productId: product.productId,
      name: product.name,
      image: product.images[0],
      price: product.price,
      quantity,
      sku: product.sku,
    });

    closeProduct();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={closeProduct}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-4 md:inset-8 lg:inset-12 bg-background rounded-lg shadow-2xl z-50 overflow-hidden"
          >
            <button
              onClick={closeProduct}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-warm-100 hover:bg-warm-200 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="h-full overflow-y-auto">
              {productLoading || !product ? (
                <div className="grid lg:grid-cols-2 min-h-full">
                  <div className="bg-warm-100 p-6 lg:p-12">
                    <Skeleton className="aspect-square rounded-lg" />
                  </div>
                  <div className="p-6 lg:p-12">
                    <Skeleton className="h-6 w-24 mb-2" />
                    <Skeleton className="h-10 w-3/4 mb-4" />
                    <Skeleton className="h-8 w-32 mb-6" />
                    <Skeleton className="h-24 w-full mb-8" />
                    <Skeleton className="h-12 w-full" />
                  </div>
                </div>
              ) : (
                <div className="grid lg:grid-cols-2 min-h-full">
                  <div className="bg-warm-100 p-6 lg:p-12">
                    <div className="sticky top-6">
                      <motion.div
                        key={selectedImage}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="aspect-square rounded-lg overflow-hidden bg-white mb-4"
                      >
                        <img
                          src={product.images[selectedImage]}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </motion.div>

                      {product.images.length > 1 && (
                        <div className="flex gap-3">
                          {product.images.map((image, index) => (
                            <button
                              key={index}
                              onClick={() => setSelectedImage(index)}
                              className={`w-20 h-20 rounded-sm overflow-hidden border-2 transition-colors ${
                                selectedImage === index
                                  ? 'border-warm-800'
                                  : 'border-transparent hover:border-warm-300'
                              }`}
                            >
                              <img
                                src={image}
                                alt={`${product.name} ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="p-6 lg:p-12">
                    <div className="max-w-lg">
                      {category && (
                        <p className="text-ochre font-medium tracking-wider uppercase text-sm mb-2">
                          {category.name}
                        </p>
                      )}

                      <h1 className="text-3xl lg:text-4xl font-serif text-warm-900 mb-4">
                        {product.name}
                      </h1>

                      <div className="flex items-center gap-3 mb-6">
                        <span className="text-2xl font-medium text-warm-900">
                          {formatPrice(product.price)}
                        </span>
                        {product.compareAtPrice && product.compareAtPrice > product.price && (
                          <>
                            <span className="text-lg text-warm-400 line-through">
                              {formatPrice(product.compareAtPrice)}
                            </span>
                            <Badge variant="destructive">
                              Save{' '}
                              {Math.round(
                                ((product.compareAtPrice - product.price) / product.compareAtPrice) *
                                  100
                              )}
                              %
                            </Badge>
                          </>
                        )}
                      </div>

                      <p className="text-warm-600 mb-8 leading-relaxed">{product.description}</p>

                      {product.metadata && (
                        <div className="grid grid-cols-2 gap-4 mb-8">
                          {product.metadata.artist && (
                            <div>
                              <p className="text-sm text-warm-500">Artist</p>
                              <p className="text-warm-800">{product.metadata.artist}</p>
                            </div>
                          )}
                          {product.metadata.technique && (
                            <div>
                              <p className="text-sm text-warm-500">Technique</p>
                              <p className="text-warm-800">{product.metadata.technique}</p>
                            </div>
                          )}
                          {product.metadata.region && (
                            <div>
                              <p className="text-sm text-warm-500">Region</p>
                              <p className="text-warm-800">{product.metadata.region}</p>
                            </div>
                          )}
                          {product.metadata.materials && (
                            <div>
                              <p className="text-sm text-warm-500">Materials</p>
                              <p className="text-warm-800">{product.metadata.materials.join(', ')}</p>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="flex items-center gap-4 mb-6">
                        <div className="flex items-center border border-warm-200 rounded-sm">
                          <button
                            onClick={() => setQuantity(Math.max(1, quantity - 1))}
                            className="p-3 hover:bg-warm-100 transition-colors"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-12 text-center font-medium">{quantity}</span>
                          <button
                            onClick={() => setQuantity(Math.min(10, quantity + 1))}
                            className="p-3 hover:bg-warm-100 transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>

                        <Button onClick={handleAddToCart} size="lg" className="flex-1">
                          <ShoppingBag className="w-5 h-5 mr-2" />
                          Add to Cart
                        </Button>
                      </div>

                      <div className="border-t border-warm-200 pt-6 space-y-4">
                        <div className="flex items-start gap-3">
                          <Truck className="w-5 h-5 text-warm-500 mt-0.5" />
                          <div>
                            <p className="font-medium text-warm-800">Free Shipping</p>
                            <p className="text-sm text-warm-500">On orders over $100</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Shield className="w-5 h-5 text-warm-500 mt-0.5" />
                          <div>
                            <p className="font-medium text-warm-800">Authenticity Guaranteed</p>
                            <p className="text-sm text-warm-500">
                              Each piece comes with a certificate
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Leaf className="w-5 h-5 text-warm-500 mt-0.5" />
                          <div>
                            <p className="font-medium text-warm-800">Eco-Friendly</p>
                            <p className="text-sm text-warm-500">Made with natural dyes</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
